import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets scroll on every client-side navigation. Mounted once, just inside
 * the Router in `App`. Cross-page navigations always land on the new page
 * already at the right scroll position — no animation — so the user never
 * sees the page scroll past them.
 *
 *   - Pathname changed, no hash → instant jump to top.
 *   - Hash present              → instant jump to the matching element id.
 *     If the id isn't found (lazy-loaded section, stale link), fall back to
 *     top.
 *
 * `requestAnimationFrame` defers the scroll until after the new route's
 * first paint so the target node has mounted by the time we look it up.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const raf = requestAnimationFrame(() => {
        const target = document.getElementById(id);
        if (target) {
          target.scrollIntoView({ behavior: 'instant' });
        } else {
          window.scrollTo({ top: 0, behavior: 'instant' });
        }
      });
      return () => cancelAnimationFrame(raf);
    }

    window.scrollTo({ top: 0, behavior: 'instant' });
    return undefined;
  }, [pathname, hash]);

  return null;
}
